import React, { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CircularProgress, Box } from '@mui/material';
import { RootState } from '../redux/store';
import { useAuth } from '../hooks/useAuth';
import { localDevAuthBypass } from '../services/auth/localDevAuthBypass';
import { isLocalDevBuild } from '../services/auth/isLocalDevBuild';

interface PrivateRouteProps {
  children: ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { isLoading, login } = useAuth();
  const location = useLocation();
  const allowLocalSandboxAccess =
    isLocalDevBuild() &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
    location.pathname.startsWith('/hours-reporting/test');
  const allowLocalDevAccess = localDevAuthBypass();

  useEffect(() => {
    // Redirect if auth check is done and user is not authenticated
    if (!allowLocalSandboxAccess && !allowLocalDevAccess && !isLoading && !isAuthenticated) {
      const isCallbackRoute = location.pathname.startsWith('/auth/');
      const isLoginRoute = location.pathname === '/login';

      if (!isCallbackRoute && !isLoginRoute) {
        // Save the current path as the return URL
        const returnUrl = location.pathname + location.search;
        // useAuth.login already logs and rethrows on failure; swallow the
        // rejection here so an unhandled promise doesn't surface twice.
        login(returnUrl).catch(() => {});
      }
    }
  }, [allowLocalSandboxAccess, allowLocalDevAccess, isLoading, isAuthenticated, location, login]);

  if (allowLocalSandboxAccess || allowLocalDevAccess) {
    return <>{children}</>;
  }

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated, show loading (will redirect to Keycloak)
  if (!isAuthenticated) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return <>{children}</>;
};

export default PrivateRoute;
